import { useState, useEffect, useRef } from 'react';
import type { ILocation, ITag} from '@map-bldr/shared';
import { useLocationTags } from '../hooks/locationTagLinkHooks';

export interface ILocationListItemProps {
  location: ILocation;
  index: number;
  availableTags: ITag[];
  isSelected: boolean;
  onSelect: (locationId: string) => void;
  onRemove: (id: string) => void;
  onCreateAndApplyTag: (locationId: string, name: string) => void;
}

export function LocationListItem({
  location,
  index,
  availableTags,
  isSelected,
  onSelect,
  onRemove,
  onCreateAndApplyTag
}: ILocationListItemProps) {
  const { data: locationTagIds = [], addTag, removeTag } = useLocationTags(location.id);
  const [address, setAddress] = useState<string>('Loading address...');
  const [showTagSelector, setShowTagSelector] = useState(false);
  const [tagInput, setTagInput] = useState<string>('');
  const itemRef = useRef<HTMLDivElement>(null);

  // Scroll into view when selected
  useEffect(() => {
    if (isSelected && itemRef.current) {
      itemRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      });
    }
  }, [isSelected]);

  useEffect(() => {
    if (!window.google?.maps?.Geocoder) {
      setAddress(`Location ${index + 1}`);
      return;
    }

    const geocoder = new google.maps.Geocoder();
    geocoder.geocode(
      { location: { lat: location.lat, lng: location.lng } },
      (results, status) => {
        if (status === 'OK' && results && results[0]) {
          setAddress(results[0].formatted_address);
        } else {
          setAddress(`Location ${index + 1}`);
        }
      }
    );
  }, [location.lat, location.lng, index]);

  const handleAddTag = (tagId: string) => {
    if (!locationTagIds.includes(tagId)) {
      addTag(tagId);
    }
    setShowTagSelector(false);
    setTagInput('');
  };

  const handleCreateAndAddTag = (name: string) => {
    // Create new tag and apply it to this location atomically
    onCreateAndApplyTag(location.id, name);
    setShowTagSelector(false);
    setTagInput('');
  };

  const handleRemoveTag = (tagId: string) => {
    removeTag(tagId);
  };

  const locationTags = locationTagIds
    .map(tagId => availableTags.find(tag => tag.id === tagId))
    .filter(tag => tag !== undefined) as ITag[];

  const unselectedTags = availableTags.filter(
    tag => !locationTagIds.includes(tag.id)
  );

  // Filter tags based on input
  const filteredTags = tagInput
    ? unselectedTags.filter(tag =>
        tag.name.toLowerCase().includes(tagInput.toLowerCase())
      )
    : unselectedTags;

  // Check if input matches an existing unselected tag exactly
  const exactMatch = unselectedTags.find(
    tag => tag.name.toLowerCase() === tagInput.toLowerCase()
  );

  // Check if tag name already exists at all (to prevent duplicates)
  const tagNameExists = availableTags.some(
    tag => tag.name.toLowerCase() === tagInput.toLowerCase()
  );

  const showCreateNew = tagInput.trim() !== '' && !tagNameExists;

  return (
    <div
      ref={itemRef}
      onClick={() => onSelect(location.id)}
      className={`bg-card border rounded-lg p-3 flex justify-between items-center cursor-pointer transition-colors ${
        isSelected ? 'border-primary border-2' : 'border-border'
      }`}
    >
      <div className="flex-1 font-mono text-sm">
        <div className="font-bold text-foreground mb-1 text-xs">
          {address}
        </div>
        <div className="text-xs text-muted-foreground leading-relaxed mb-2">
          <div>Lat: {location.lat.toFixed(5)}</div>
          <div>Lng: {location.lng.toFixed(5)}</div>
        </div>

        {/* Tags section */}
        <div className="flex flex-wrap items-center gap-1 mt-2">
          {locationTags.map(tag => (
            <div
              key={tag.id}
              className="inline-flex items-center gap-1 bg-background border border-border rounded px-2 py-0.5 text-xs"
            >
              <span className="text-foreground">#{tag.name}</span>
              <button
                onClick={() => handleRemoveTag(tag.id)}
                className="bg-transparent text-muted-foreground hover:text-foreground border-none cursor-pointer text-xs p-0 leading-none"
                title="Remove tag"
              >
                ×
              </button>
            </div>
          ))}

          {/* Add tag button */}
          {showTagSelector ? (
            <div className="relative inline-block">
              <input
                type="text"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && tagInput.trim()) {
                    e.preventDefault();
                    // If there's an exact match, add that tag
                    if (exactMatch) {
                      handleAddTag(exactMatch.id);
                    }
                    // Otherwise if tag doesn't exist, create it
                    else if (showCreateNew) {
                      handleCreateAndAddTag(tagInput.trim());
                    }
                  } else if (e.key === 'Escape') {
                    setShowTagSelector(false);
                    setTagInput('');
                  }
                }}
                onBlur={() => {
                  // Delay to allow click on suggestions
                  setTimeout(() => {
                    setShowTagSelector(false);
                    setTagInput('');
                  }, 200);
                }}
                autoFocus
                placeholder="Type tag name..."
                className="bg-background text-foreground border border-primary rounded px-2 py-0.5 text-xs font-mono w-32 focus:outline-none focus:ring-1 focus:ring-primary"
              />

              {/* Dropdown with suggestions */}
              {(filteredTags.length > 0 || showCreateNew) && (
                <div className="absolute top-full left-0 mt-1 bg-background border border-border rounded shadow-lg z-10 min-w-[150px] max-h-40 overflow-y-auto">
                  {filteredTags.map(tag => (
                    <button
                      key={tag.id}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        handleAddTag(tag.id);
                      }}
                      className="w-full text-left px-3 py-1.5 text-xs hover:bg-primary/10 text-foreground border-none cursor-pointer font-mono"
                    >
                      #{tag.name}
                    </button>
                  ))}

                  {showCreateNew && (
                    <button
                      onMouseDown={(e) => {
                        e.preventDefault();
                        handleCreateAndAddTag(tagInput.trim());
                      }}
                      className="w-full text-left px-3 py-1.5 text-xs hover:bg-primary/10 text-primary border-none cursor-pointer font-mono border-t border-border"
                    >
                      + Create "{tagInput.trim()}"
                    </button>
                  )}
                </div>
              )}
            </div>
          ) : (
            <button
              onClick={() => setShowTagSelector(true)}
              className="bg-primary/10 text-primary hover:bg-primary hover:text-primary-foreground border border-primary rounded px-2 py-0.5 text-xs cursor-pointer transition-colors font-medium"
              title="Add tag"
            >
              + tag
            </button>
          )}
        </div>
      </div>

      <div className="flex items-center gap-2 flex-shrink-0 ml-3">
        <button
          onClick={(e) => {
            e.stopPropagation();
            onRemove(location.id);
          }}
          className="bg-transparent text-muted-foreground hover:text-foreground border-none rounded w-6 h-6 cursor-pointer text-lg flex items-center justify-center flex-shrink-0 transition-colors"
          title="Remove location"
        >
          ×
        </button>
      </div>
    </div>
  );
}
